'use client';

import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/decodo/components/atoms/Toggle/useCMSToggle';
import VideoStrapi, {
  VideoStrapiProps,
} from '@/decodo/components/atoms/Video/VideoStrapi';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContentDC';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

import ProductCardItem from './ProductCardItem';

export interface ProductCardsItemProps {
  icon: ImageStrapiProps;
  title: string;
  subtitle?: string;
  description: RichTextFieldStrapiProps;
  button: LinkButtonStrapiProps;
  videoLink: VideoStrapiProps;
  documentLink?: string;
}

export interface CardsWithHeaderStrapiProps {
  header?: BlockHeader;
  toggle?: ToggleCMSProps;
  cards: ProductCardsItemProps[];
  product_cards_groups: {
    data: {
      attributes: {
        productCardsElements: ProductCardsItemProps[];
      };
    }[];
  };
}

export default function ProductCardsStrapi({
  block,
  ...rest
}: BlockComponentModel<CardsWithHeaderStrapiProps>) {
  const { cards, header, toggle, product_cards_groups } = block;
  const groupedCards =
    product_cards_groups?.data?.flatMap(
      (group) => group.attributes?.productCardsElements || []
    ) ?? [];

  const rawCards = groupedCards.length > 0 ? groupedCards : cards;

  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof rawCards
  >(rawCards, toggle);

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.PRODUCT_CARDS}>
      <div className='flex'>
        <BlockHeaderContent
          {...header}
          topChildren={ToggleNode ? ToggleNode() : undefined}
        >
          <div
            className={cn(
              'flex w-full flex-wrap gap-5',
              'm:grid-cols-3 md:grid md:gap-[23px]'
            )}
          >
            {rawCards.map((card) => (
              <div
                key={card.title}
                className={cn(
                  'h-full w-full',
                  filteredToggleList?.length &&
                    !filteredToggleList?.some(
                      (item) => card.title === item.title
                    ) &&
                    'hide-content'
                )}
              >
                <ProductCardItem
                  key={`${card.title}`}
                  icon={
                    <ImageStrapi
                      block={{ ...card.icon }}
                      inner={{ classNames: { image: 'w-6 h-6' } }}
                    />
                  }
                  title={card.title}
                  subtitle={card.subtitle}
                  description={
                    card.description && (
                      <div data-tracker-component='description'>
                        <RichTextFieldStrapi
                          block={card.description}
                          classNames={{ paragraph: 'text-body-l' }}
                        />
                      </div>
                    )
                  }
                  button={
                    card.button ? (
                      <LinkButtonStrapi
                        block={card?.button}
                        inner={{
                          slim: false,
                          variant: 'filled',
                          color: 'primary',
                          classNames: {
                            inner: 'justify-center',
                            root: 'dark:text-base-dark-white',
                          },
                        }}
                      />
                    ) : undefined
                  }
                  videoLink={
                    card.videoLink ? (
                      <VideoStrapi block={card.videoLink} />
                    ) : undefined
                  }
                  documentLink={
                    card.documentLink ? card.documentLink : undefined
                  }
                />
              </div>
            ))}
          </div>
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
